import { apiClient } from "./apiClient";
import type { CurrentUser } from "../types";

/**
 * Default anonymous user returned when API call fails or user is not logged in
 */
const DEFAULT_ANONYMOUS_USER: CurrentUser = {
  id: null,
  identity: 0,
  email: null,
  email_confirmed: false,
  first_name: null,
  last_name: null,
  full_name: null,
  is_logged_in: false,
  is_staff: false,
  switches: {},
  current_account_id: null,
  signup: null,
};

/**
 * Fetches the current user from the API and transforms the response.
 * This shared utility is used by both the React hook (client-side) and server-side route handlers.
 *
 * Logic:
 * - Calls the API to get current user data
 * - Constructs full_name from first_name and last_name if needed
 * - Returns a default anonymous user if the API call fails
 *
 * @param options - Optional configuration
 * @param options.baseUrl - Base URL for server-side requests (defaults to relative path for client-side)
 * @param options.cookies - Cookie string to forward for server-side requests
 * @returns Promise resolving to CurrentUser
 */
export async function fetchCurrentUser(options?: {
  baseUrl?: string;
  cookies?: string;
}): Promise<CurrentUser> {
  // Server-side path: use fetch with forwarded cookies
  if (options?.baseUrl && options?.cookies !== undefined) {
    const url = `${options.baseUrl}/api/supportfrontend/v1/proxy/current-user`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          cookie: options.cookies,
          "Content-Type": "application/json",
        },
        cache: "no-store",
      });

      if (!response.ok) {
        console.warn(
          `Failed to fetch current user (server-side): ${response.status}`,
        );
        return DEFAULT_ANONYMOUS_USER;
      }

      const data = await response.json();

      return {
        ...data,
        // Construct full_name from first_name and last_name
        full_name:
          `${data.first_name || ""} ${data.last_name || ""}`.trim() || null,
      };
    } catch (error) {
      console.error("Error fetching current user (server-side):", error);
      return DEFAULT_ANONYMOUS_USER;
    }
  }

  // Client-side path: use apiClient
  const response = await apiClient.getCurrentUser();

  if (!response) {
    return DEFAULT_ANONYMOUS_USER;
  }

  const currentUser: CurrentUser = {
    ...response,
    // Construct full_name from first_name and last_name
    full_name:
      `${response.first_name || ""} ${response.last_name || ""}`.trim() || null,
  };

  return currentUser;
}
